import { AppState } from '../../types/store';
import { createSelector } from 'reselect';
import {
  SectionLayout,
  SectionLayoutZones,
  SectionLayoutZone,
} from '../../types/layout';
import { getModuleType, getModules } from './modules';
import { isCurrentUserProfileOwner } from '../selectors/users';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession';

export const getSectionLayout = (state: AppState, section): SectionLayout =>
  state.layout[section] || {};

export const getSectionLayoutZones = (
  state: AppState,
  section
): SectionLayoutZones => getSectionLayout(state, section).zones || {};

export const getSectionLayoutVersion = (state: AppState, section): number =>
  getSectionLayout(state, section).version || 0;

export const getSectionLayoutZone = (
  state: AppState,
  section,
  zone
): SectionLayoutZone => getSectionLayoutZones(state, section)[zone] || {};

export const getAllInstalledModuleIds = (state: AppState, section) => {
  const zones = Object.keys(getSectionLayout(state, section).zones || {});
  return zones.reduce(
    (acc, zone) => acc.concat(getInstalledModuleIds(state, section, zone)),
    [] as number[]
  );
};
export const getInstalledModuleIds = (state: AppState, section, zone) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  if (!zoneLayout) {
    return [];
  }
  return zoneLayout.installedModulesIds || [];
};

export const getInstalledModuleTypes = (state: AppState, section, zone) =>
  getInstalledModuleIds(state, section, zone).map(moduleId =>
    getModuleType(state, moduleId)
  );

export const getInstallableModules = createSelector(
  [getSectionLayoutZone, getInstalledModuleTypes],
  (zoneLayout, installedModuleTypes) => {
    if (!zoneLayout) {
      return [];
    }

    const installedModuleCounts: Record<string, number> = {};
    installedModuleTypes.forEach(moduleType => {
      if (installedModuleCounts[moduleType]) {
        installedModuleCounts[moduleType]++;
      } else {
        installedModuleCounts[moduleType] = 1;
      }
    });

    return (zoneLayout.installableModuleTypes || []).map(moduleInfo => ({
      ...moduleInfo,
      installedCount: installedModuleCounts[moduleInfo.moduleName] ?? 0,
    }));
  }
);

export const getInstallableModulesInfo = createSelector(
  [getInstallableModules],
  installableModules =>
    installableModules.filter(({ installedCount, limit, restrict }) => {
      const coreLimit =
        restrict && restrict.priv === 'core' ? restrict.limit : 0;
      return installedCount < Math.max(limit, coreLimit);
    })
);

export const getInstallableModuleTypes = createSelector(
  [getInstallableModulesInfo],
  installableModulesInfo =>
    installableModulesInfo.map(moduleInfo => moduleInfo.moduleName)
);

export const getCanCurrentUserEditModule = createSelector(
  [
    getModules,
    getInstallableModules,
    isCurrentUserProfileOwner,
    getCurrentUserHasCore,
    (_state, _section, _zone, moduleId) => moduleId,
  ],
  (modules, installableModules, isOwner, hasCore, moduleId) => {
    if (!isOwner || !modules || !modules[moduleId]) {
      return false;
    }
    const { type: moduleName } = modules[moduleId];
    const installableModule = installableModules.find(
      module => moduleName === module.moduleName
    );
    if (!installableModule || !installableModule.restrict) {
      return true;
    }
    // trying to find the position of the given moduleId in an array of
    // modulesIds of the same type, sorted by installation order
    const installedModules = Object.keys(modules)
      .filter(id => modules[id].type === moduleName)
      .map(id => parseInt(id))
      .sort();
    const installedIndex = installedModules.findIndex(id => id === moduleId);
    return (
      installedIndex <
      Math.max(
        installableModule.limit,
        hasCore ? installableModule.restrict.limit : 0
      )
    );
  }
);

export const getIsInstallingModule = (state: AppState, section, zone) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  return zoneLayout.isInstallingModule;
};

export const getCurrentlyInstalledModulePostion = (
  state: AppState,
  section,
  zone
) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  return zoneLayout.currentlyInstalledModulePostion;
};
